import { CircleCheck } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { selectActiveOrgRole, selectSessionUser } from '@/features/auth/slices/sessionSlice'
import { epochLabel } from '@/features/projects/format'
import { useAppDispatch, useAppSelector } from '@/store'
import {
  addMember,
  clearMemberError,
  removeMember,
  selectCanWriteProject,
  selectMembersError,
  selectMembersLoading,
  selectProjectDetail,
  selectProjectMembers
} from '../slices/projectDetailSlice'
import { ProjectInviteDialog } from './ProjectInviteDialog'
import { ProjectPendingInvites } from './ProjectPendingInvites'

/** 24-character ObjectId hex — a CLIENT-SIDE SHAPE check only; existence is the server's call. */
const OBJECT_ID_PATTERN = /^[0-9a-fA-F]{24}$/

interface AddForm {
  userId: string
  role: string
  accessLevel: 'WRITE' | 'READ'
}

/**
 * The roster panel (AC-4). `side` and `accessLevel` always render from the
 * SERVER row (INV-9) — the add form deliberately sends no `side`; the server
 * stamps it from the user's durable client binding and refuses a contradicting
 * explicit value, so a picker here could only manufacture 400s. Actions hide
 * for read-only callers (the server projection decides).
 */
export function ProjectMembersPanel({ orgId, projectId }: { orgId: string; projectId: string }) {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  const members = useAppSelector(selectProjectMembers)
  const membersLoading = useAppSelector(selectMembersLoading)
  const memberError = useAppSelector(selectMembersError)
  const canWrite = useAppSelector(selectCanWriteProject)
  const orgRole = useAppSelector(selectActiveOrgRole)
  const project = useAppSelector(selectProjectDetail)
  const projectName = project?.name ?? projectId
  const sessionUser = useAppSelector(selectSessionUser)
  // Invitations are an org-admin act (the server gates org admin + project
  // WRITE); the panel only hides what it would refuse.
  const canInvite = canWrite && (orgRole === 'owner' || orgRole === 'admin')
  const orgName = sessionUser?.organisations.find((org) => org.orgId === orgId)?.orgName ?? orgId
  const inviteTriggerRef = useRef<HTMLButtonElement>(null)
  const [inviteStatus, setInviteStatus] = useState<{ count: number; email: string } | null>(null)

  const [formOpen, setFormOpen] = useState(false)
  const [form, setForm] = useState<AddForm>({ userId: '', role: '', accessLevel: 'WRITE' })
  const [formError, setFormError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const onAddClicked = async (): Promise<void> => {
    if (!OBJECT_ID_PATTERN.test(form.userId.trim())) {
      setFormError(t('members.userIdInvalid'))
      return
    }
    setFormError(null)
    setAdding(true)
    const result = await dispatch(
      addMember({
        orgId,
        projectId,
        input: {
          userId: form.userId.trim(),
          role: form.role.trim() === '' ? undefined : form.role.trim(),
          accessLevel: form.accessLevel
        }
      })
    )
    setAdding(false)
    if (addMember.fulfilled.match(result)) {
      setFormOpen(false)
      setForm({ userId: '', role: '', accessLevel: 'WRITE' })
    }
  }

  const onRemoveClicked = (userId: string | undefined, memberId: string | undefined): void => {
    if (userId === undefined) return
    if (!window.confirm(t('members.confirmRemove'))) return
    void dispatch(removeMember({ orgId, projectId, userId: userId ?? memberId ?? '' }))
  }

  return (
    <section aria-label={t('members.panelTitle')} className="rounded-lg border border-border bg-card px-4 py-3.5">
      <div className="flex items-center justify-between gap-3 max-sm:flex-col max-sm:items-stretch">
        <SectionLabel>{t('members.panelTitle')}</SectionLabel>
        {canWrite && (
          <span className="flex items-center gap-3 max-sm:flex-col max-sm:items-stretch">
            <button
              type="button"
              onClick={() => setFormOpen((open) => !open)}
              className="text-xs font-medium text-accent-foreground hover:underline max-sm:min-h-[44px] max-sm:w-full"
            >
              {t('members.add')}
            </button>
            {canInvite && (
              <ProjectInviteDialog
                orgId={orgId}
                orgName={orgName}
                projectId={projectId}
                projectName={projectName}
                triggerRef={inviteTriggerRef}
                onAllSent={(count, email) => setInviteStatus({ count, email })}
              />
            )}
          </span>
        )}
      </div>

      {inviteStatus !== null && (
        <p role="status" data-testid="project-invite-sent" className="mt-2 flex items-start gap-1.5 text-sm text-done">
          <CircleCheck aria-hidden className="mt-0.5 size-4 shrink-0" />
          <span className="min-w-0 break-words">
            {t('members.invite.sentAll', { count: inviteStatus.count, email: inviteStatus.email })}
          </span>
        </p>
      )}

      {formOpen && canWrite && (
        <div className="mt-2 rounded-md border border-border px-3 py-2.5">
          <div className="grid gap-2 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <label htmlFor="member-user-id" className="block text-xs text-muted-foreground">
                {t('members.userId')}
              </label>
              <input
                id="member-user-id"
                value={form.userId}
                onChange={(event) => setForm((f) => ({ ...f, userId: event.target.value }))}
                placeholder={t('members.userIdPlaceholder')}
                className="mt-0.5 w-full rounded-md border border-input bg-card px-2.5 py-1.5 font-mono text-sm text-foreground"
              />
            </div>
            <div>
              <label htmlFor="member-access" className="block text-xs text-muted-foreground">
                {t('members.accessLevel')}
              </label>
              <select
                id="member-access"
                value={form.accessLevel}
                onChange={(event) =>
                  setForm((f) => ({ ...f, accessLevel: event.target.value as 'WRITE' | 'READ' }))
                }
                className="mt-0.5 w-full rounded-md border border-input bg-card px-2.5 py-1.5 text-sm text-foreground"
              >
                <option value="WRITE">{t('members.access.WRITE')}</option>
                <option value="READ">{t('members.access.READ')}</option>
              </select>
            </div>
            <div className="sm:col-span-3">
              <label htmlFor="member-role" className="block text-xs text-muted-foreground">
                {t('members.role')}
              </label>
              <input
                id="member-role"
                value={form.role}
                onChange={(event) => setForm((f) => ({ ...f, role: event.target.value }))}
                placeholder={t('members.rolePlaceholder')}
                className="mt-0.5 w-full rounded-md border border-input bg-card px-2.5 py-1.5 text-sm text-foreground"
              />
            </div>
          </div>
          {formError !== null && (
            <p role="alert" className="mt-1.5 text-xs text-rose-ink">
              {formError}
            </p>
          )}
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setFormOpen(false)}
              className="rounded-md border border-input px-2.5 py-1 text-xs font-medium text-foreground hover:bg-muted"
            >
              {t('members.cancel')}
            </button>
            <button
              type="button"
              onClick={() => void onAddClicked()}
              disabled={adding || membersLoading}
              className="rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
              data-testid="member-add-submit"
            >
              {adding ? t('members.adding') : t('members.submit')}
            </button>
          </div>
        </div>
      )}

      {memberError !== null && (
        <p role="alert" className="mt-2 rounded-md border border-blocked bg-blocked-bg px-3 py-2 text-sm text-foreground">
          {t('members.serverError')}: {memberError.message}
          <button
            type="button"
            onClick={() => dispatch(clearMemberError())}
            className="ml-2 text-xs font-medium text-accent-foreground hover:underline"
          >
            ×
          </button>
        </p>
      )}

      {members.length === 0 ? (
        <p className="mt-1.5 text-sm text-muted-foreground">
          {membersLoading ? t('loading') : t('members.empty')}
        </p>
      ) : (
        <ul className="mt-1.5">
          {members.map((member) => (
            <li
              key={member.userId ?? member.id ?? member.name}
              className="v-row flex flex-wrap items-center gap-x-3 gap-y-1 py-2"
            >
              <span
                aria-hidden
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-people-bg text-[0.625rem] font-semibold text-people-ink"
              >
                {member.initials ?? '—'}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-foreground">
                  {member.name ?? '—'}
                  {member.role !== undefined && (
                    <span className="ml-1.5 text-xs text-muted-foreground">{member.role}</span>
                  )}
                </span>
                {member.addedAt !== undefined && (
                  <span className="block text-xs text-muted-foreground">
                    {t('members.addedAt', { date: epochLabel(member.addedAt) })}
                  </span>
                )}
              </span>
              <StatusPill tone="neutral">
                {member.side === undefined
                  ? t('members.sideFIRM')
                  : member.side === 'CLIENT'
                    ? t('members.sideCLIENT')
                    : t('members.sideFIRM')}
              </StatusPill>
              <StatusPill tone={member.accessLevel === 'READ' ? 'neutral' : 'proposed'}>
                {t(`members.access.${member.accessLevel ?? 'WRITE'}`)}
              </StatusPill>
              {canWrite && member.userId !== undefined && (
                <button
                  type="button"
                  onClick={() => onRemoveClicked(member.userId, member.id)}
                  className="text-xs font-medium text-accent-foreground hover:underline"
                >
                  {t('members.remove')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canInvite && (
        <ProjectPendingInvites
          orgId={orgId}
          projectId={projectId}
          projectName={projectName}
          inviteTriggerRef={inviteTriggerRef}
        />
      )}
      {canWrite && !canInvite && (
        <p data-testid="project-invite-admin-only" className="mt-2 text-xs text-muted-foreground">
          {t('members.inviteAdminOnly')}
        </p>
      )}
      {!canWrite && <p className="mt-2 text-xs text-muted-foreground">{t('members.readonly')}</p>}
    </section>
  )
}
